import {
  chooseType,
  halvingSteps,
  looksBlank,
  nextAttempt,
  planResize,
  QualityLadder,
  type Attempt,
  type Size,
} from './imagePlan.ts';

/**
 * Turning whatever came out of a phone into something worth sending.
 *
 * A modern phone photograph is three to six megabytes. Sending that from a shop would
 * take longer than the shop visit, and fifty of them would be most of the household's
 * storage allowance — so the shrinking happens here, before anything leaves the device,
 * and the server only ever has to validate what arrives.
 *
 * <b>The canvas round trip strips EXIF, and that is a feature.</b> A photo taken at home
 * carries the home's coordinates. Nothing that leaves the phone should, and re-encoding
 * through a canvas discards every tag rather than us having to remember to.
 *
 * Everything decidable lives in imagePlan.ts. What is left here is the browser work,
 * which is why this file is thin: it can only be tested against stubs of the very APIs it
 * exists to call.
 */

/** The photo could not be decoded at all — almost always HEIC outside Safari. */
export class UnsupportedImageError extends Error {
  constructor() {
    super("That photo format isn't supported on this device. Taking a new one usually works.");
    this.name = 'UnsupportedImageError';
  }
}

/** Compressed as hard as we are willing to and still too big to send. */
export class ImageTooLargeError extends Error {
  constructor() {
    super("That photo is too detailed to shrink. Try taking it again, a little further back.");
    this.name = 'ImageTooLargeError';
  }
}

export interface CompressedImage {
  blob: Blob;
  width: number;
  height: number;
}

/**
 * Whether this browser can actually encode WebP.
 *
 * Asked once and remembered. `toBlob` does not fail for a type it cannot encode — it
 * quietly hands back a PNG — so the only way to know is to encode something and look at
 * what comes out.
 */
let webpSupport: Promise<boolean> | null = null;

function canEncodeWebp(): Promise<boolean> {
  webpSupport ??= new Promise<boolean>((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;

    canvas.toBlob(
      (blob) => resolve(blob?.type === 'image/webp'),
      'image/webp',
      0.5,
    );
  });

  return webpSupport;
}

/** Test seam: the detection is cached for the life of the page, which tests must undo. */
export function resetWebpSupportForTests(): void {
  webpSupport = null;
}

export async function compressImage(file: Blob): Promise<CompressedImage> {
  const source = await decode(file);

  try {
    const type = chooseType(await canEncodeWebp());

    let attempt: Attempt = { longEdge: planLongEdge(source), quality: QualityLadder[0] };

    for (;;) {
      const size = planResize(source.width, source.height, attempt.longEdge);
      const canvas = drawDownTo(source, size);
      const blob = await encode(canvas, type, attempt.quality);

      // A blank canvas encodes to almost nothing, and Safari produces one silently for
      // a large enough source. Half the size costs a little sharpness; storing an empty
      // rectangle and reporting success costs the whole point of the feature.
      if (looksBlank(blob.size, size.width, size.height)) {
        if (attempt.longEdge <= 600) {
          throw new ImageTooLargeError();
        }

        attempt = { longEdge: Math.round(attempt.longEdge / 2), quality: attempt.quality };
        continue;
      }

      const verdict = nextAttempt({ attempt, bytes: blob.size });

      if (verdict === 'accept') {
        return { blob, width: size.width, height: size.height };
      }

      if (verdict === 'give-up') {
        throw new ImageTooLargeError();
      }

      attempt = verdict;
    }
  } finally {
    source.close();
  }
}

interface DecodedImage {
  width: number;
  height: number;
  draw: (context: CanvasRenderingContext2D, size: Size) => void;
  close: () => void;
}

/**
 * Decodes the file, upright.
 *
 * No EXIF parsing: every current engine defaults `image-orientation: from-image`, so a
 * photo taken sideways reports its rotated dimensions and draws the right way up without
 * being asked. Reading the orientation tag ourselves and rotating would double-apply it.
 */
async function decode(file: Blob): Promise<DecodedImage> {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.src = url;

  try {
    await image.decode();
  } catch {
    // HEIC in anything but Safari lands here, as does a file that is not an image at
    // all despite its name.
    URL.revokeObjectURL(url);
    throw new UnsupportedImageError();
  }

  return {
    width: image.naturalWidth,
    height: image.naturalHeight,
    draw: (context, size) => context.drawImage(image, 0, 0, size.width, size.height),
    close: () => URL.revokeObjectURL(url),
  };
}

function planLongEdge(source: DecodedImage): number {
  const { width, height } = planResize(source.width, source.height);

  return Math.max(width, height);
}

/**
 * Draws the source down to `size`, in halving steps.
 *
 * Browsers resample with a small kernel, so a single draw from 4000px to 1200px samples
 * about one pixel in three — which on the photographed label this feature exists for
 * looks like a smear. Each halving keeps every pixel contributing to the next.
 */
function drawDownTo(source: DecodedImage, size: Size): HTMLCanvasElement {
  const longest = Math.max(source.width, source.height);
  const target = Math.max(size.width, size.height);

  let canvas = canvasOf(source.width, source.height);
  let context = contextOf(canvas);
  source.draw(context, { width: source.width, height: source.height });

  for (const step of halvingSteps(longest, target)) {
    const stepSize = planResize(source.width, source.height, step);
    const next = canvasOf(stepSize.width, stepSize.height);

    contextOf(next).drawImage(canvas, 0, 0, stepSize.width, stepSize.height);
    canvas = next;
  }

  const final = canvasOf(size.width, size.height);
  context = contextOf(final);
  context.drawImage(canvas, 0, 0, size.width, size.height);

  return final;
}

function canvasOf(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  return canvas;
}

function contextOf(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const context = canvas.getContext('2d');

  if (!context) {
    throw new UnsupportedImageError();
  }

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';

  return context;
}

function encode(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new ImageTooLargeError())),
      type,
      quality,
    );
  });
}
